import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import api from '../services/api';

const STATUS_BADGES = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const ParentAccountRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const load = () => {
    setLoading(true);
    api.get('/parent-portal/requests', { params: filter ? { status: filter } : {} })
      .then((res) => { if (res.data.success) setRequests(res.data.data); })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const handleApprove = async (id) => {
    const result = await Swal.fire({
      title: 'Approuver cette demande ?',
      text: 'Un email d\'activation sera envoyé au parent.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Approuver',
      cancelButtonText: 'Annuler',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await api.post(`/parent-portal/requests/${id}/approve`);
      if (res.data.success) {
        toast.success(res.data.message);
        load();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
  };

  const handleReject = async (id) => {
    const result = await Swal.fire({
      title: 'Refuser cette demande',
      input: 'textarea',
      inputLabel: 'Motif (optionnel)',
      showCancelButton: true,
      confirmButtonText: 'Refuser',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await api.post(`/parent-portal/requests/${id}/reject`, { reason: result.value });
      if (res.data.success) {
        toast.success(res.data.message);
        load();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Demandes de compte parents</h1>
        <p className="text-sm text-gray-500">Validez ou refusez les demandes d'accès à l'espace parents</p>
      </div>

      <div className="flex gap-2 mb-4">
        {['pending', 'approved', 'rejected', ''].map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              filter === s ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s === 'pending' ? 'En attente' : s === 'approved' ? 'Approuvées' : s === 'rejected' ? 'Refusées' : 'Toutes'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full" />
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">Aucune demande</div>
      ) : (
        <div className="space-y-4">
          {requests.map((r) => (
            <div key={r.id} className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{r.prenom} {r.nom}</h3>
                  <p className="text-sm text-gray-500">{r.email} {r.telmob && `— ${r.telmob}`}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Demandé le {new Date(r.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_BADGES[r.status]}`}>
                  {r.status === 'pending' ? 'En attente' : r.status === 'approved' ? 'Approuvée' : 'Refusée'}
                </span>
              </div>

              {r.children_data?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Enfants identifiés</p>
                  <ul className="text-sm text-gray-700">
                    {r.children_data.map((c, i) => (
                      <li key={i}>• {c.prenom} {c.nom} ({c.matricule})</li>
                    ))}
                  </ul>
                </div>
              )}

              {r.rejection_reason && (
                <p className="mt-2 text-sm text-red-600">Motif : {r.rejection_reason}</p>
              )}

              {r.status === 'pending' && (
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => handleApprove(r.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                  >
                    Approuver
                  </button>
                  <button
                    onClick={() => handleReject(r.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                  >
                    Refuser
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParentAccountRequestsPage;
